import React, { useState, useMemo } from 'react';
import { Carousel, Image, Grid } from 'antd';
import { carouselStyle, imageStyle, carouselContainerClass } from './styles/imageSliderStyles';
import { IPlanPhoto } from '../../types/plans';

const { useBreakpoint } = Grid;

interface ImageSliderProps {
  photos: IPlanPhoto[];
  fallbackImage?: string;
  isHidden?: boolean;
  autoSlideInterval?: number;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const DEFAULT_PLACEHOLDER = 'https://via.placeholder.com/400x300?text=No+Image';

const extractImageUrls = (photos: IPlanPhoto[], fallbackImage?: string): string[] => {
  if (!photos?.length) {
    return fallbackImage ? [fallbackImage] : [DEFAULT_PLACEHOLDER];
  }

  const validUrls = photos
    .filter(photo => photo?.original || photo?.compressed?.url)
    .map(photo => photo.compressed?.url || photo.original || '');

  return validUrls.length > 0 ? validUrls : [fallbackImage || DEFAULT_PLACEHOLDER];
};

const ImageComponent: React.FC<{
  src: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}> = ({ src, onClick }) => (
  <Image
    src={src}
    alt=""
    style={imageStyle}
    preview={false}
    placeholder
    fallback={DEFAULT_PLACEHOLDER}
    onClick={onClick}
  />
);

export const ImageSlider: React.FC<ImageSliderProps> = ({
  photos,
  fallbackImage,
  isHidden = false,
  autoSlideInterval = 5000,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const images = useMemo(() => extractImageUrls(photos, fallbackImage), [photos, fallbackImage]);

  const containerStyle = useMemo(
    () => ({
      ...carouselStyle,
      opacity: isHidden ? 0.6 : 1.0,
    }),
    [isHidden]
  );

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const shouldShowArrows = isMobile || isHovered;
  const shouldAutoplay = autoSlideInterval > 0 && !isHovered;

  if (images.length === 1) {
    return (
      <div
        className={carouselContainerClass}
        style={containerStyle}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Image viewer">
        <ImageComponent src={images[0]} onClick={onClick} />
      </div>
    );
  }

  return (
    <div
      className={carouselContainerClass}
      style={containerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label="Image carousel">
      <Carousel
        autoplay={shouldAutoplay}
        autoplaySpeed={autoSlideInterval}
        arrows={shouldShowArrows}
        dots
        infinite
        draggable={isMobile}
        effect="scrollx">
        {images.map((src, index) => (
          <ImageComponent key={index} src={src} onClick={onClick} />
        ))}
      </Carousel>
    </div>
  );
};
